// News index and article pages. Posts are plain text with a small markdown subset
// (headings, bullet lists, paragraphs) and are rendered as React text, never as HTML.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type ApiError } from "../api";
import { Icon } from "../icons";
import { EmptyState, Skeleton, useApi } from "../ui";
import { ErrorState } from "../ui/kit";
import { AreaGate, SiteShell } from "./SiteShell";
import { PostText } from "./postText";

type NewsItem = { slug: string; title: string; summary: string; coverUrl: string | null; publishedAt: string; author?: string | null };
type NewsPage = { items: NewsItem[]; more: boolean };
type Article = NewsItem & { body: string };

const longDate = (value: string) => new Intl.DateTimeFormat(undefined, { dateStyle: "long" }).format(new Date(value));
const shortDate = (value: string) => new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));

/* ---------- Index ---------- */

export function NewsIndex() {
  return <AreaGate id="public.news"><SiteShell><NewsIndexBody/></SiteShell></AreaGate>;
}

function NewsIndexBody() {
  const first = useApi<NewsPage>("/api/v1/public/news?limit=12");
  const [older, setOlder] = useState<NewsItem[]>([]);
  const [more, setMore] = useState<boolean | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState("");
  const items = [...(first.data?.items || []), ...older];
  const hasMore = more ?? Boolean(first.data?.more);
  const loadOlder = () => {
    const last = items[items.length - 1];
    if (!last) return;
    setLoadingMore(true); setMoreError("");
    api<NewsPage>(`/api/v1/public/news?limit=12&before=${encodeURIComponent(last.publishedAt)}`)
      .then((page) => { setOlder((current) => [...current, ...page.items]); setMore(page.more); })
      .catch((error: ApiError) => setMoreError(error.error?.message || "Older posts could not be loaded. Try again."))
      .finally(() => setLoadingMore(false));
  };
  const [lead, ...rest] = items;
  return <main id="main">
    <div className="container page-top"><header className="page-head"><h1>News</h1><p>Updates, events, and changes to the server, written by the team.</p></header></div>
    <section className="band" aria-label="Posts">
      <div className="container">
        {first.loading && !first.data ? <Skeleton rows={4} label="Loading news"/>
          : first.error ? <ErrorState error={first.error} fallback="News could not be loaded right now. Try again in a few minutes."/>
          : !lead ? <EmptyState icon="megaphone" title="No posts yet" description="The team has not published any news yet. Update notes and event dates will show up here." action={<div className="empty-actions"><Link className="button" to="/">Back to the home page</Link></div>}/>
          : <>
            <article className={`news-lead ${lead.coverUrl ? "has-cover" : ""}`}>
              {lead.coverUrl && <img src={lead.coverUrl} alt="" width={1280} height={720}/>}
              <div className="news-lead-copy">
                <div className="news-lead-title">
                  <time dateTime={lead.publishedAt}>{longDate(lead.publishedAt)}</time>
                  <h2><Link className="stretched" to={`/news/${lead.slug}`}>{lead.title}</Link></h2>
                </div>
                <div className="news-lead-text">
                  {lead.summary && <p>{lead.summary}</p>}
                  <span className="news-more" aria-hidden="true">Read the post<Icon name="arrowRight" size={16}/></span>
                </div>
              </div>
            </article>
            {rest.length > 0 && <>
              <h2 className="news-list-title">Earlier posts</h2>
              <ol className="news-list">{rest.map((post) => <li key={post.slug}>
                <time dateTime={post.publishedAt}>{shortDate(post.publishedAt)}</time>
                <div><h3><Link className="stretched" to={`/news/${post.slug}`}>{post.title}</Link></h3>{post.summary && <p>{post.summary}</p>}</div>
                <Icon name="arrowRight" size={18}/>
              </li>)}</ol>
            </>}
            {(hasMore || moreError) && <div className="news-older">
              {moreError && <p className="field-error" role="alert">{moreError}</p>}
              {hasMore && <button type="button" className="button" onClick={loadOlder} disabled={loadingMore}>{loadingMore ? "Loading…" : "Older posts"}</button>}
            </div>}
          </>}
      </div>
    </section>
  </main>;
}

/* ---------- Article ---------- */

export function NewsArticle() {
  return <AreaGate id="public.news"><SiteShell><ArticleBody/></SiteShell></AreaGate>;
}

function ArticleBody() {
  const { slug = "" } = useParams();
  const state = useApi<{ post: Article }>(`/api/v1/public/news/${encodeURIComponent(slug)}`);
  const post = state.data?.post;
  if (state.loading && !post) return <main id="main"><div className="container article"><Skeleton rows={5} label="Loading the post"/></div></main>;
  if (state.error?.error?.code === "not_found" || (!state.error && !post)) return <main id="main">
    <div className="container page-top"><header className="page-head"><h1>We could not find that post</h1><p>It may have been taken down, or the link has a typo.</p></header></div>
    <div className="container news-missing"><Link className="button primary" to="/news"><Icon name="arrowLeft" size={16}/>All news</Link></div>
  </main>;
  if (state.error || !post) return <main id="main"><div className="container article"><h1 className="sr-only">News</h1><ErrorState error={state.error} fallback="This post could not be loaded right now. Try again in a few minutes."/></div></main>;
  return <main id="main">
    <article className="container article">
      <Link className="article-back" to="/news"><Icon name="arrowLeft" size={16}/>All news</Link>
      <header className="article-head">
        <h1>{post.title}</h1>
        <p className="article-meta"><time dateTime={post.publishedAt}>{longDate(post.publishedAt)}</time>{post.author && <><span aria-hidden="true"> · </span>{post.author}</>}</p>
      </header>
      {post.summary && <p className="article-summary">{post.summary}</p>}
      {post.coverUrl && <img className="article-cover" src={post.coverUrl} alt="" width={1280} height={720}/>}
      <div className="article-body"><PostText text={post.body}/></div>
    </article>
    <MoreNews slug={post.slug}/>
  </main>;
}

function MoreNews({ slug }: { slug: string }) {
  const state = useApi<NewsPage>("/api/v1/public/news?limit=4");
  const others = (state.data?.items || []).filter((item) => item.slug !== slug).slice(0, 3);
  if (!others.length) return null;
  return <section className="band raised" aria-labelledby="more-news">
    <div className="container">
      <div className="section-head"><div><h2 id="more-news">More from the team</h2></div><Link className="section-link" to="/news">All news<Icon name="arrowRight" size={16}/></Link></div>
      <ol className="news-list">{others.map((post) => <li key={post.slug}>
        <time dateTime={post.publishedAt}>{shortDate(post.publishedAt)}</time>
        <div><h3><Link className="stretched" to={`/news/${post.slug}`}>{post.title}</Link></h3>{post.summary && <p>{post.summary}</p>}</div>
        <Icon name="arrowRight" size={18}/>
      </li>)}</ol>
    </div>
  </section>;
}

